/**
 * 执行上下文查看面板组件
 *
 * 在调试模式下显示当前执行上下文数据
 * 包括：task_prompt、workspace_context、loop_context、info_store
 * 参考: docs/ragflow_web 工作流设计
 */

import React, { memo, useState, useMemo, useCallback } from 'react';
import { clsx } from 'clsx';
import { useExecutionStore } from '../../store/workflowStore';
import { ExecutionState } from '../../types/workflow';

// ==================== 类型定义 ====================

export interface ExecutionContextPanelProps {
  /** 自定义类名 */
  className?: string;
  /** 是否默认展开 */
  defaultExpanded?: boolean;
  /** 是否可折叠 */
  collapsible?: boolean;
  /** 是否显示标题 */
  showTitle?: boolean;
  /** 是否紧凑模式 */
  compact?: boolean;
  /** 变量引用高亮回调 */
  onVariableClick?: (variable: string) => void;
}

// ==================== 子组件 ====================

/** 上下文项组件 */
interface ContextItemProps {
  label: string;
  value: unknown;
  depth?: number;
  defaultExpanded?: boolean;
  highlightVariables?: boolean;
  onVariableClick?: (variable: string) => void;
}

const ContextItem = memo<ContextItemProps>(({
  label,
  value,
  depth = 0,
  defaultExpanded = false,
  highlightVariables = true,
  onVariableClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  const isObject = typeof value === 'object' && value !== null;
  const isArray = Array.isArray(value);
  const isEmpty = isObject && Object.keys(value as object).length === 0;
  
  // 渲染值
  const renderValue = useCallback(() => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">null</span>;
    }
    
    if (typeof value === 'boolean') {
      return <span className="text-paper-700">{value ? 'true' : 'false'}</span>;
    }
    
    if (typeof value === 'number') {
      return <span className="text-success-600">{value}</span>;
    }
    
    if (typeof value === 'string') {
      // 高亮变量引用
      if (highlightVariables) {
        const parts = value.split(/(\{[^}]+\})/g);
        return (
          <span>
            {parts.map((part, index) => {
              if (part.startsWith('{') && part.endsWith('}')) {
                const variable = part.slice(1, -1);
                return (
                  <span
                    key={index}
                    className="px-1 bg-paper-100 text-paper-800 rounded text-xs font-mono cursor-pointer hover:bg-paper-200"
                    onClick={() => onVariableClick?.(variable)}
                    title={`点击复制: ${variable}`}
                  >
                    {part}
                  </span>
                );
              }
              return <span key={index} className="text-gray-700">{part}</span>;
            })}
          </span>
        );
      }
      return <span className="text-gray-700">"{value}"</span>;
    }
    
    return <span className="text-gray-500">{String(value)}</span>;
  }, [value, highlightVariables, onVariableClick]);

  // 渲染对象/数组
  if (isObject && !isEmpty) {
    const entries = Object.entries(value as Record<string, unknown>);
    
    return (
      <div className="context-item">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 w-full text-left hover:bg-gray-50 rounded px-1 py-0.5 group"
        >
          <svg
            className={clsx('w-3 h-3 text-gray-400 transition-transform', isExpanded && 'rotate-90')}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <span className="text-xs text-gray-400 ml-auto">
            {isArray ? `[${entries.length}]` : `{${entries.length}}`}
          </span>
        </button>
        
        {isExpanded && (
          <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-gray-100 pl-2">
            {entries.map(([key, val]) => (
              <ContextItem
                key={key}
                label={key}
                value={val}
                depth={depth + 1}
                highlightVariables={highlightVariables}
                onVariableClick={onVariableClick}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // 渲染基本值
  return (
    <div className="flex items-center gap-2 py-0.5 px-1 hover:bg-gray-50 rounded">
      <span className="text-sm font-medium text-gray-600 min-w-[100px]">{label}</span>
      <span className="text-gray-300">:</span>
      <span className="text-sm break-all">{renderValue()}</span>
    </div>
  );
});

ContextItem.displayName = 'ContextItem';

/** 上下文分区组件 */
interface ContextSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
}

const ContextSection = memo<ContextSectionProps>(({
  title,
  icon,
  children,
  defaultExpanded = true,
  isEmpty = false,
  emptyMessage = '暂无数据',
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 w-full px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <svg
          className={clsx('w-3 h-3 text-gray-400 transition-transform', isExpanded && 'rotate-90')}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {icon}
        <span className="text-sm font-medium text-gray-700">{title}</span>
      </button>
      
      {isExpanded && (
        <div className="p-3 bg-white">
          {isEmpty ? (
            <div className="text-sm text-gray-400 italic text-center py-2">
              {emptyMessage}
            </div>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
});

ContextSection.displayName = 'ContextSection';

// ==================== 主组件 ====================

export const ExecutionContextPanel = memo<ExecutionContextPanelProps>(({
  className,
  defaultExpanded = true,
  collapsible = true,
  showTitle = true,
  compact = false,
  onVariableClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  // 从 store 获取执行上下文
  const executionState = useExecutionStore((state) => state.executionState);
  const context = useExecutionStore((state) => state.context);
  
  // 是否在调试状态
  const isDebugging = useMemo(() => {
    return [
      ExecutionState.RUNNING,
      ExecutionState.PAUSED,
      ExecutionState.STEP_WAITING,
    ].includes(executionState);
  }, [executionState]);

  // 上下文是否为空
  const isContextEmpty = useMemo(() => {
    return (
      !context.task_prompt &&
      !context.workspace &&
      !context.loop &&
      Object.keys(context.info).length === 0
    );
  }, [context]);

  // 处理变量点击
  const handleVariableClick = useCallback((variable: string) => {
    // 复制变量引用到剪贴板
    navigator.clipboard.writeText(`{${variable}}`);
    onVariableClick?.(variable);
  }, [onVariableClick]);

  // 如果不在调试状态且没有上下文数据，不显示面板
  if (!isDebugging && isContextEmpty && executionState !== ExecutionState.PAUSED) {
    return null;
  }

  return (
    <div className={clsx(
      'bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden',
      compact ? 'text-xs' : 'text-sm',
      className,
    )}>
      {/* 标题栏 */}
      {showTitle && (
        <div
          className={clsx(
            'flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200',
            collapsible && 'cursor-pointer hover:bg-gray-100',
          )}
          onClick={() => collapsible && setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            {collapsible && (
              <svg
                className={clsx('w-4 h-4 text-gray-400 transition-transform', isExpanded && 'rotate-90')}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
            <svg className="w-4 h-4 text-paper-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span className="font-medium text-gray-700">执行上下文</span>
            {isDebugging && executionState === ExecutionState.PAUSED && (
              <span className="text-xs text-warning-600 bg-warning-100 px-1.5 py-0.5 rounded">
                已暂停 - 可编辑
              </span>
            )}
          </div>
        </div>
      )}

      {/* 内容区域 */}
      {isExpanded && (
        <div className="p-3 space-y-3 max-h-[60vh] overflow-y-auto">
          {/* 任务 Prompt */}
          <ContextSection
            title="任务 Prompt"
            icon={
              <svg className="w-4 h-4 text-paper-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.364a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            }
            isEmpty={!context.task_prompt}
            emptyMessage="未设置任务 Prompt"
          >
            <div className="text-sm text-gray-700 whitespace-pre-wrap p-2 bg-gray-50 rounded">
              {context.task_prompt}
            </div>
          </ContextSection>

          {/* 工作区上下文 */}
          <ContextSection
            title="工作区信息"
            icon={
              <svg className="w-4 h-4 text-success-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            }
            isEmpty={!context.workspace}
            emptyMessage="未加载工作区信息"
          >
            {context.workspace && (
              <div className="space-y-1">
                <ContextItem label="ID" value={context.workspace.workspace_id} />
                <ContextItem label="名称" value={context.workspace.workspace_name} />
                <ContextItem label="总章节数" value={context.workspace.total_chapters} />
                <ContextItem label="类型" value={context.workspace.genre} />
              </div>
            )}
          </ContextSection>

          {/* 循环上下文 */}
          <ContextSection
            title="循环变量"
            icon={
              <svg className="w-4 h-4 text-warning-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            }
            isEmpty={!context.loop}
            emptyMessage="当前不在循环容器中"
          >
            {context.loop && (
              <div className="space-y-1">
                <ContextItem label="当前迭代" value={`${context.loop.iteration_index + 1} / ${context.loop.total_iterations}`} />
                <ContextItem label="当前章节" value={context.loop.current_chapter} />
                <ContextItem label="章节范围" value={`${context.loop.chapter_start} - ${context.loop.chapter_end}`} />
                <div className="mt-2 p-2 bg-gray-50 rounded">
                  <div className="text-xs text-gray-500 mb-1">可引用变量:</div>
                  <div className="flex flex-wrap gap-1">
                    {['iteration_index', 'chapter_start', 'chapter_end', 'current_chapter'].map((v) => (
                      <code
                        key={v}
                        className="px-1.5 py-0.5 bg-paper-100 text-paper-800 rounded text-xs cursor-pointer hover:bg-paper-200"
                        onClick={() => handleVariableClick(`loop.${v}`)}
                        title="点击复制"
                      >
                        {'{loop.' + v + '}'}
                      </code>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </ContextSection>

          {/* 信息存储 */}
          <ContextSection
            title="信息存储 (Info)"
            icon={
              <svg className="w-4 h-4 text-paper-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            isEmpty={Object.keys(context.info).length === 0}
            emptyMessage="暂无信息节点数据"
          >
            <div className="space-y-2">
              {Object.entries(context.info).map(([nodeName, data]) => (
                <div key={nodeName} className="border border-gray-100 rounded p-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-paper-700">{nodeName}</span>
                    <code
                      className="px-1 py-0.5 bg-paper-50 text-paper-700 rounded text-xs cursor-pointer hover:bg-paper-100"
                      onClick={() => handleVariableClick(`info.${nodeName}`)}
                      title="点击复制"
                    >
                      {'{info.' + nodeName + '.*}'}
                    </code>
                  </div>
                  <ContextItem
                    label=""
                    value={data}
                    defaultExpanded={false}
                    highlightVariables={true}
                    onVariableClick={handleVariableClick}
                  />
                </div>
              ))}
            </div>
          </ContextSection>
        </div>
      )}
    </div>
  );
});

ExecutionContextPanel.displayName = 'ExecutionContextPanel';

export default ExecutionContextPanel;
